import React from "react";
import { Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Typography, Button } from "@mui/material";
import { Block } from "@mui/icons-material";
import { usePermissions } from "../../hooks/usePermissions";

const normalizeRoleSlug = (rawRole) => {
  if (!rawRole) return "";
  const s = String(rawRole).trim().toLowerCase().replace(/[_\s]+/g, "");
  if (s === "institute") return "admin";
  if (s === "superadmin" || s === "superadministrator") return "superadmin";
  return s;
};

/**
 * Route guard: use permissionSlug (preferred) or allowedRoles.
 * - permissionSlug: checks hasPermission(permissionSlug) - supports custom roles
 * - allowedRoles: legacy role check - use permissionSlug for new routes
 */
const RoleBasedRoute = ({ allowedRoles = [], permissionSlug }) => {
  const { user } = useSelector((state) => state.auth);
  const { hasVisiblePermission, isLoading } = usePermissions();

  if (isLoading) return null;

  if (permissionSlug) {
    if (!hasVisiblePermission(permissionSlug)) {
      return <AccessDenied />;
    }
    return <Outlet />;
  }

  const rawRole = user?.role?.slug || user?.role;
  const normalizedUserRole = normalizeRoleSlug(rawRole);
  const normalizedAllowedRoles = (allowedRoles || []).map((role) => normalizeRoleSlug(role));

  if (!normalizedAllowedRoles.includes(normalizedUserRole)) {
    return <AccessDenied />;
  }

  return <Outlet />;
};

const AccessDenied = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "60vh",
      textAlign: "center",
      p: 3,
    }}
  >
    <Block sx={{ fontSize: 80, color: "error.main", mb: 2 }} />
    <Typography variant="h4" gutterBottom fontWeight={600}>
      Access Denied
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      You don't have permission to access this page.
    </Typography>
    <Button variant="contained" href="/dashboard">
      Go to Dashboard
    </Button>
  </Box>
);

export default RoleBasedRoute;
